import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * Second step of dispatcher sign-in: a 6-digit SMS code.
 * Props: pendingToken, phoneHint, onSuccess(user), onCancel()
 */
export const OtpVerify = ({ pendingToken, phoneHint, onSuccess, onCancel }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(30);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { setErr("Enter the 6-digit code."); return; }
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-otp", { pending_token: pendingToken, code: code.trim() });
      toast.success(`Verified — welcome, ${r.data.name}`);
      onSuccess(r.data);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr("");
    try {
      await api.post("/auth/resend-otp", { pending_token: pendingToken });
      toast.success("A new code was texted.");
      setCooldown(30);
      setCode("");
      inputRef.current?.focus();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="otp-verify-step">
      <div className="overline mb-3">Two-step verification</div>
      <h1 className="font-serif text-3xl mb-2">Enter the code</h1>
      <p className="text-[#A1A1AA] text-sm mb-6" data-testid="otp-phone-hint">
        Dispatcher access requires verification. We texted a 6-digit code to{" "}
        <span className="text-[#D4AF37]">{phoneHint || "the dispatch phone"}</span>. It expires in 5 minutes.
      </p>
      <form onSubmit={verify} className="space-y-3">
        <input
          data-testid="otp-code-input"
          ref={inputRef}
          type="text" inputMode="numeric" autoComplete="one-time-code"
          maxLength={6} value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="••••••"
          className="w-full bg-black border border-white/10 px-4 py-3 text-center text-2xl tracking-[0.6em] font-mono focus:border-[#D4AF37] outline-none"
        />
        {err && <div data-testid="otp-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}
        <button data-testid="otp-verify-button" type="submit" disabled={busy || code.length !== 6}
          className="w-full bg-[#D4AF37] text-black py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:opacity-90 disabled:opacity-60 transition">
          {busy ? "Verifying…" : "Verify & sign in"}
        </button>
      </form>
      <div className="flex items-center justify-between mt-4">
        <button data-testid="otp-resend-button" type="button" onClick={resend} disabled={cooldown > 0}
          className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed">
          {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
        </button>
        {onCancel && (
          <button data-testid="otp-cancel-button" type="button" onClick={onCancel}
            className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] hover:text-white">
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
};

export default OtpVerify;
